// Robox V2 — Types de domínio
// Estáveis, versionados via SCHEMA_VERSION em persistence.ts

export type ContentType = 'video' | 'reading' | 'exercise' | 'review' | 'other';
export type ContentStatus = 'todo' | 'doing' | 'done' | 'skipped';
export type SessionStatus = 'planned' | 'active' | 'completed' | 'abandoned';
export type GoalType = 'cert' | 'smart' | 'okr' | 'habit';
export type GoalStatus = 'active' | 'paused' | 'achieved' | 'abandoned';
/** área da vida — taxonomia FIXA de propósito: dado comparável entre usuários (benchmark F5) */
export type GoalArea = 'carreira' | 'educacao' | 'saude' | 'financas' | 'pessoal';

/** estado de revisão espaçada SM-2 de um conteúdo concluído (algoritmo em core/sm2.ts) */
export interface ReviewState {
  reps: number; // revisões corretas em sequência
  ease: number; // fator de facilidade (EF, mín 1.3)
  interval_days: number;
  due_date: string; // yyyy-mm-dd local da próxima revisão
  last_quality?: number; // 0-5 da última resposta
  last_reviewed_at?: string; // ISO
}

export interface Content {
  id: string;
  goalId?: string; // vinculo com meta
  title: string;
  type: ContentType;
  duration_min: number; // duração planejada em min
  difficulty: 1 | 2 | 3 | 4 | 5;
  order: number; // ordem sugerida
  status: ContentStatus;
  source?: string; // origem: "curso X", "livro Y"
  tags?: string[];
  notes?: string;
  review?: ReviewState; // presente após o conteúdo ser concluído
  created_at: string; // ISO
  updated_at: string;
}

export interface Session {
  id: string;
  content_id: string;
  goal_id?: string;
  planned_at: string; // ISO — quando o usuário planejou fazer
  started_at?: string;
  completed_at?: string;
  planned_duration_min: number;
  actual_duration_min?: number;
  status: SessionStatus;
  focus_score?: number; // 1-5 auto-relatado no fim
  notes?: string;
}

export interface Plan {
  id: string;
  goal_id: string;
  start_date: string; // ISO date only
  end_date: string;
  weekly_capacity_h: number; // horas por semana disponíveis
  distribution: { [dow: number]: number }; // 0-6 → horas
  created_at: string;
}

export interface Goal {
  id: string;
  type: GoalType;
  title: string; // ex: "CPA-20 2026"
  description?: string;
  status: GoalStatus;
  priority: 1 | 2 | 3; // 1=principal
  area?: GoalArea; // ausente em metas antigas → 'carreira'
  parent_id?: string; // submeta de um grupo (1 nível, ex: Certificações 2026 → CPA-20)
  target_date?: string; // ISO date
  target_hours?: number; // ex: 120h para CPA-20
  // meta numérica (peso, dinheiro, nota...) — progresso = (atual−início)/(alvo−início)
  start_value?: number; // ex: 90 (kg)
  current_value?: number; // ex: 84 — atualizado via check-in
  target_value?: number; // ex: 80
  unit?: string; // ex: "kg", "R$"
  created_at: string;
  updated_at: string;
}

export interface ScoreLog {
  date: string; // ISO date only (yyyy-mm-dd)
  overall: number; // 0-100
  execution: number;
  consistency: number;
  energy: number;
  learning: number;
}

export type RoutineKind = 'sleep' | 'work' | 'exercise' | 'meal' | 'commitment' | 'other';

/** Evento de rotina: sono, trabalho, exercício, compromisso — recorrente ou data única */
export interface RoutineEvent {
  id: string;
  title: string;
  kind: RoutineKind;
  /** recorrente: dias da semana (0=dom..6=sáb). Ignorado se `date` definido */
  days_of_week?: number[];
  /** evento único nesta data local (yyyy-mm-dd) */
  date?: string;
  /** minutos desde 00:00 local (ex: 23h = 1380) */
  start_min: number;
  /** pode atravessar a meia-noite (sono 23h + 480min) */
  duration_min: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Settings {
  pomodoro_focus_min: number;
  pomodoro_short_break_min: number;
  pomodoro_long_break_min: number;
  pomodoro_cycles_before_long: number;
  sound_enabled: boolean;
  auto_start_break: boolean;
  notifications_enabled: boolean;
  weekly_capacity_h: number; // default para novos planos
  active_goal_id?: string;
}

// Evento rastreado (definição completa em core/events.ts)
export interface TrackedEvent {
  id: string;
  ts: string;
  type: string;
  payload: Record<string, unknown>;
}

// Estado global
export interface AppState {
  contents: Content[];
  sessions: Session[];
  plans: Plan[];
  goals: Goal[];
  routines: RoutineEvent[];
  scoreLogs: ScoreLog[];
  events: TrackedEvent[];
  settings: Settings;
}

export const DEFAULT_SETTINGS: Settings = {
  pomodoro_focus_min: 25,
  pomodoro_short_break_min: 5,
  pomodoro_long_break_min: 15,
  pomodoro_cycles_before_long: 4,
  sound_enabled: true,
  auto_start_break: false,
  notifications_enabled: false,
  weekly_capacity_h: 12,
};

export const INITIAL_STATE: AppState = {
  contents: [],
  sessions: [],
  plans: [],
  goals: [],
  routines: [],
  scoreLogs: [],
  events: [],
  settings: DEFAULT_SETTINGS,
};
